import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BarChart3, TrendingUp, DollarSign, Users, Target, Calendar, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useI18n } from '../contexts/I18nContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reports = () => {
  const { t } = useI18n();
  const [stats, setStats] = useState({
    contacts_count: 0,
    companies_count: 0,
    deals_count: 0,
    total_deal_value: 0,
    total_revenue: 0,
    conversion_rate: 0,
    campaigns_sent: 0,
    avg_open_rate: 0,
    tasks_pending: 0,
  });
  const [deals, setDeals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pipeline, setPipeline] = useState({ by_stage: {} });
  const [activity, setActivity] = useState({ last_30_days: [] });
  const [emailPerf, setEmailPerf] = useState({ total: 0, sent: 0, open_rate: 0, click_rate: 0 });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, dealsRes, contactsRes, pipelineRes, activityRes, emailRes] = await Promise.all([
        axios.get(`${API}/reports/overview`, { withCredentials: true }).catch(() => axios.get(`${API}/dashboard/stats`, { withCredentials: true })),
        axios.get(`${API}/deals`, { withCredentials: true }),
        axios.get(`${API}/contacts`, { withCredentials: true }),
        axios.get(`${API}/reports/pipeline`, { withCredentials: true }).catch(() => ({ data: { by_stage: {} } })),
        axios.get(`${API}/reports/activity`, { withCredentials: true }).catch(() => ({ data: { last_30_days: [] } })),
        axios.get(`${API}/reports/email`, { withCredentials: true }).catch(() => ({ data: { total: 0, sent: 0, open_rate: 0, click_rate: 0 } })),
      ]);

      setStats(statsRes.data);
      setDeals(dealsRes.data);
      setContacts(contactsRes.data);
      setPipeline(pipelineRes.data || { by_stage: {} });
      setActivity(activityRes.data || { last_30_days: [] });
      setEmailPerf(emailRes.data || { total: 0, sent: 0, open_rate: 0, click_rate: 0 });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate metrics
  const dealsByStage = deals.reduce((acc, deal) => {
    acc[deal.stage] = (acc[deal.stage] || 0) + 1;
    return acc;
  }, {});

  const contactsByStatus = contacts.reduce((acc, contact) => {
    acc[contact.status] = (acc[contact.status] || 0) + 1;
    return acc;
  }, {});

  const revenueByMonth = stats.revenue_by_month && Object.keys(stats.revenue_by_month).length > 0
    ? Object.entries(stats.revenue_by_month).reduce((acc, [ym, value]) => {
        const [y, m] = ym.split('-');
        const date = new Date(parseInt(y), parseInt(m) - 1, 1);
        const label = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        acc[label] = value;
        return acc;
      }, {})
    : deals
      .filter(d => d.stage === 'closed_won')
      .reduce((acc, deal) => {
        const month = new Date(deal.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        acc[month] = (acc[month] || 0) + deal.amount;
        return acc;
      }, {});

  const avgDealSize = deals.length > 0 ? (stats.avg_deal_size || (stats.total_deal_value / deals.length)).toFixed(0) : 0;
  const winRate = deals.filter(d => d.stage === 'closed_won').length / (deals.filter(d => d.stage === 'closed_won' || d.stage === 'closed_lost').length || 1) * 100;

  const exportReport = (type) => {
    let csvContent = '';

    if (type === 'deals') {
      csvContent = 'Title,Amount,Stage,Created At\n';
      deals.forEach(deal => {
        csvContent += `"${deal.title}",${deal.amount},${deal.stage},${new Date(deal.created_at).toLocaleDateString()}\n`;
      });
    } else if (type === 'contacts') {
      csvContent = 'Name,Email,Company,Status,Created At\n';
      contacts.forEach(contact => {
        csvContent += `"${contact.name}","${contact.email || ''}","${contact.company || ''}",${contact.status},${new Date(contact.created_at).toLocaleDateString()}\n`;
      });
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bissa_${type}_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0176D3]"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('pages.reports.title','Reports & Analytics')}</h1>
        <p className="text-gray-600 mt-2">{t('pages.reports.subtitle','Track performance and make data-driven decisions')}</p>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">{t('pages.reports.overview','Overview')}</TabsTrigger>
          <TabsTrigger value="sales">{t('pages.reports.sales','Sales Pipeline')}</TabsTrigger>
          <TabsTrigger value="contacts">{t('pages.reports.contacts','Contacts')}</TabsTrigger>
          <TabsTrigger value="email">{t('pages.reports.email','Email Performance')}</TabsTrigger>
          <TabsTrigger value="export">{t('pages.reports.export','Export Data')}</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Revenue</p>
                    <p className="text-3xl font-bold text-gray-900">${stats.total_revenue.toLocaleString()}</p>
                    <p className="text-xs text-green-600 mt-1">+12.5% from last month</p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-lg">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Win Rate</p>
                    <p className="text-3xl font-bold text-gray-900">{(stats.conversion_rate || winRate).toFixed(1)}%</p>
                    <p className="text-xs text-green-600 mt-1">+3.2% from last month</p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Target className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Avg Deal Size</p>
                    <p className="text-3xl font-bold text-gray-900">${avgDealSize}</p>
                    <p className="text-xs text-orange-600 mt-1">-2.1% from last month</p>
                  </div>
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Contacts</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.contacts_count}</p>
                    <p className="text-xs text-green-600 mt-1">+{contacts.filter(c => {
                      const created = new Date(c.created_at);
                      const monthAgo = new Date();
                      monthAgo.setMonth(monthAgo.getMonth() - 1);
                      return created > monthAgo;
                    }).length} this month</p>
                  </div>
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <Users className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Revenue by Month */}
          <Card>
            <CardHeader>
              <CardTitle>{t('pages.reports.revenueByMonth','Revenue by Month')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(revenueByMonth).map(([month, revenue]) => (
                  <div key={month} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium">{month}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-48 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-[#0176D3] h-2 rounded-full"
                          style={{ width: `${(revenue / Math.max(...Object.values(revenueByMonth))) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-bold w-24 text-right">${revenue.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
                {Object.keys(revenueByMonth).length === 0 && (
                  <p className="text-center text-gray-500 py-8">{t('pages.reports.noRevenue','No revenue data yet')}</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales Pipeline Tab */}
        <TabsContent value="sales" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Deals by Stage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(pipeline.by_stage || dealsByStage).map(([stage, data]) => {
                    const count = typeof data === 'object' ? data.count : data;
                    const total = deals.length || Object.values(dealsByStage).reduce((s, v) => s + v, 0);
                    return (
                      <div key={stage} className="flex items-center justify-between">
                        <span className="text-sm capitalize">{stage.replace('_', ' ')}</span>
                        <div className="flex items-center gap-3">
                          <div className="w-40 bg-gray-200 rounded-full h-2">
                            <div className="bg-[#0176D3] h-2 rounded-full" style={{ width: `${(count / Math.max(1, total)) * 100}%` }} />
                          </div>
                          <span className="text-sm font-bold w-8">{count}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Pipeline Value by Stage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(pipeline.by_stage || {}).map(([stage, v]) => (
                    <div key={stage} className="flex items-center justify-between">
                      <span className="text-sm capitalize">{stage.replace('_', ' ')}</span>
                      <span className="text-sm font-bold">${(v.value || 0).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Activity (Last 30 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              {activity.last_30_days.length === 0 ? (
                <div className="text-sm text-gray-500">No recent activity.</div>
              ) : (
                <div className="grid grid-cols-7 gap-2">
                  {activity.last_30_days.map((d, idx) => (
                    <div key={idx} className="text-center">
                      <div className="h-12 w-2 mx-auto bg-[#0176D3] rounded" style={{ height: `${Math.min(100, d.count * 8)}px` }} />
                      <div className="text-[10px] text-gray-500 mt-1">{d.type.slice(0,3)}</div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Contacts by Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-6">
                {Object.entries(contactsByStatus).map(([status, count]) => (
                  <div key={status} className="text-center">
                    <p className="text-4xl font-bold text-gray-900">{count}</p>
                    <p className="text-sm text-gray-600 capitalize mt-1">{status}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent Contacts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {contacts.slice(0, 10).map(contact => (
                  <div key={contact.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div>
                      <p className="font-medium">{contact.name}</p>
                      <p className="text-sm text-gray-600">{contact.email || 'No email'}</p>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(contact.created_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Performance Tab */}
        <TabsContent value="email" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="text-sm text-gray-600">Open Rate</div>
                <div className="text-3xl font-bold">{emailPerf.open_rate.toFixed(1)}%</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="text-sm text-gray-600">Click Rate</div>
                <div className="text-3xl font-bold">{emailPerf.click_rate.toFixed(1)}%</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="text-sm text-gray-600">Sent (All Time)</div>
                <div className="text-3xl font-bold">{emailPerf.sent}</div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Opens vs Clicks (Mini)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="text-xs mb-1">Opens</div>
                  <div className="h-2 bg-gray-200 rounded">
                    <div className="h-2 bg-green-500 rounded" style={{ width: `${Math.min(100, emailPerf.open_rate)}%` }} />
                  </div>
                </div>
                <div>
                  <div className="text-xs mb-1">Clicks</div>
                  <div className="h-2 bg-gray-200 rounded">
                    <div className="h-2 bg-blue-500 rounded" style={{ width: `${Math.min(100, emailPerf.click_rate)}%` }} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Export Tab */}
        <TabsContent value="export" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Export Reports</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">Deals Report</h4>
                    <p className="text-sm text-gray-600">Export all deals with details</p>
                  </div>
                  <Button onClick={() => exportReport('deals')} className="bg-[#0176D3] hover:bg-[#0155A8]">
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </div>

              <div className="p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">Contacts Report</h4>
                    <p className="text-sm text-gray-600">Export all contacts with details</p>
                  </div>
                  <Button onClick={() => exportReport('contacts')} className="bg-[#0176D3] hover:bg-[#0155A8]">
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
